/**
 * 이상한 방 - 매치 점수 (찾은 점수 · 내 구획 점수 · 순위)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.5절(점수 - 술래는 발견, 체인저는 안 들킨 것)
 *            `이상한방-상태모델-v0.1.md` 11.3절(점수는 서버가 쥔다) · 12절(늦은 쪽은 득점하지 않는다)
 *
 * ## 찾은 점수에는 새 규칙이 없다
 *
 * i의 찾은 점수는 `hunt.computeScoreBreakdown({ found: 내가 찾은 것, changes: 내가 찾을 수 있던 것, maxCombo, hasPerfect })`다.
 * 공유 `found`에서 `by === i`를 거른 것이 싱글의 `found`와 같은 모양이라 그대로 넘긴다. `changes`가 전체가 아니라
 * **내가 찾을 수 있던 것**인 이유는 퍼펙트 때문이다(M11) - 내 구획의 것을 내가 못 찾으므로 전체를 넘기면
 * 남의 구획을 다 찾은 사람도 영원히 퍼펙트가 아니다. 속도 보너스·콤보 배수를 여기서 다시 적으면 두 번째 사본이다.
 *
 * ## 구획 점수 하나만 새 규칙이다 - 그리고 이제 **전원이** 받는다
 *
 * `내 구획의 미발견 × tuning.changerPointsPerUnfound`. 4.5절의 "숨긴 것이 안 들키면 득점"을 숫자로 굳힌 것이고,
 * 감점은 없다 - 내 것이 전부 들키면 0점이지 음수가 아니다(`hunt.ts` "점수판의 숫자는 위로만").
 * M10까지는 그 라운드의 한 사람만 받았지만 M11에서는 전원이 자기 구획을 갖는다 - 한 사람의 라운드 점수는
 * **찾은 점수 + 내 구획 점수**의 합이고, 그 둘이 서로 다른 축(잘 찾았는가 / 잘 숨겼는가)이라 더한다.
 * 걸기(M7 "내 것 N개는 아무도 못 찾는다")가 맞으면 구획 점수에 배수가 붙고, 틀리면 배수만 없다. 배수와
 * 맞았는지의 비교는 `raise.ts`(`wagerMultiplier`)의 것이다 - 찾은 사람의 걸기와 같은 배수여야 하므로 이 파일에 `× 2`는 없다.
 *
 * ## 순위는 같은 점수면 같은 등수다
 *
 * 술래 넷이 전부 0점인 판에서 입장 순서로 1·2·3·4등을 매기면 그 등수는 규칙이 아니라 우연이다.
 * 표시 순서만 입장 순서로 굳혀(결정론) 같은 점수는 같은 `rank`를 받는다.
 */

import type { PlayerId } from '../../core/types'
import { computeScoreBreakdown } from '../hunt'
import { wagerMultiplier } from '../raise'
import type { Tuning } from '../tuning'
import { findableChanges, foundBy, seatsInOrder, unfoundOf } from './matchSelectors'
import type { MatchPlayer, MatchState, RankingRow } from './matchTypes'

/* --- 라운드 점수 ------------------------------------------------------------------ */

/** 한 사람이 **찾아서** 번 점수. 싱글의 `computeScore`와 **같은 함수**를 부른다. */
export function hunterRoundScore(state: MatchState, id: PlayerId, tuning: Tuning): number {
  const player = state.players[id]
  if (player === undefined) return 0
  return computeScoreBreakdown(
    {
      found: foundBy(state, id),
      changes: findableChanges(state, id),
      maxCombo: player.maxCombo,
      hasPerfect: player.hasPerfect,
    },
    tuning,
  ).total
}

/**
 * 한 사람이 **안 들켜서** 번 점수(유일한 새 규칙). 걸기가 맞았으면 배수(`raise.wagerMultiplier`), 틀렸으면 그대로 - 벌은 없다.
 * 걸기가 좌석에 있는 이유는 M11에서 전원이 걸 수 있기 때문이다(`MatchPlayer.wager`).
 */
export function zoneRoundScore(state: MatchState, id: PlayerId, tuning: Tuning): number {
  const unfound = unfoundOf(state, id).length
  return unfound * tuning.changerPointsPerUnfound * wagerMultiplier(unfound, state.players[id]?.wager ?? null)
}

/** 한 사람의 라운드 점수 = 찾은 점수 + 내 구획 점수(머리말). 역할이 없으므로 갈래도 없다. */
export function roundScoreFor(state: MatchState, player: MatchPlayer, tuning: Tuning): number {
  return hunterRoundScore(state, player.id, tuning) + zoneRoundScore(state, player.id, tuning)
}

/* --- 순위 ------------------------------------------------------------------------ */

/**
 * 점수 내림차순, 같은 점수는 같은 등수(1·1·3). 표시 순서는 입장 순서라 같은 입력은 같은 표를 만든다.
 * `key`를 받는 이유는 라운드 순위와 누적 순위가 **같은 규칙**이어야 하기 때문이다.
 */
export function rankRows(
  rows: readonly Readonly<{ playerId: PlayerId; roundScore: number; totalScore: number }>[],
  key: 'roundScore' | 'totalScore',
): readonly RankingRow[] {
  const sorted = [...rows].sort((a, b) => b[key] - a[key])
  let rank = 0
  let previous: number | null = null
  return sorted.map((row, index) => {
    if (previous === null || row[key] !== previous) {
      rank = index + 1
      previous = row[key]
    }
    return { playerId: row.playerId, roundScore: row.roundScore, totalScore: row.totalScore, rank }
  })
}

/** 결과 진입 시 한 번. 좌석마다 라운드 점수를 굳히고 누적에 얹은 뒤 라운드 순위표를 낸다. */
export function settleRound(
  state: MatchState,
  tuning: Tuning,
): Readonly<{ state: MatchState; ranking: readonly RankingRow[] }> {
  const players: Record<PlayerId, MatchPlayer> = {}
  const rows: { playerId: PlayerId; roundScore: number; totalScore: number }[] = []

  for (const player of seatsInOrder(state)) {
    const roundScore = roundScoreFor(state, player, tuning)
    const settled: MatchPlayer = { ...player, roundScore, totalScore: player.totalScore + roundScore }
    players[player.id] = settled
    rows.push({ playerId: player.id, roundScore, totalScore: settled.totalScore })
  }

  return { state: { ...state, players }, ranking: rankRows(rows, 'roundScore') }
}

/** 누적 순위표. 결과 화면과 `matchView`가 라운드 순위 옆에 놓는다. */
export function cumulativeRanking(state: MatchState): readonly RankingRow[] {
  return rankRows(
    seatsInOrder(state).map((player) => ({
      playerId: player.id,
      roundScore: player.roundScore,
      totalScore: player.totalScore,
    })),
    'totalScore',
  )
}
